import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { updateBdcDc } from "../../../services";
import { BdcBranch, BdcDc } from "../../../types/bdc.type";
import BranchChip from "../Branch/BranchChip";
import { branchLabel } from "../Branch/branches";
import { pcs } from "../format";
import { useBdcDcs } from "../useBdcDcs";
import { checkArCustomerCode, dcUpdateBody, UNBOUND } from "./updateBody";

function DcRow({
  dc,
  branches,
  canWrite,
  onSaved,
}: Readonly<{ dc: BdcDc; branches: BdcBranch[]; canWrite: boolean; onSaved: (dc: BdcDc) => void }>) {
  const [name, setName] = useState(dc.dc_name);
  const [companyId, setCompanyId] = useState(dc.company_id ?? UNBOUND);
  const [arCode, setArCode] = useState(dc.ar_customer_code ?? "");
  const [saving, setSaving] = useState(false);
  const arError = checkArCustomerCode(arCode);
  const body = dcUpdateBody(dc, name, companyId, arCode);
  const dirty = Object.keys(body).length > 0 && !arError;
  const unbound = !dc.company_id;
  // a DC bound to a branch this viewer cannot see still shows (and keeps) its binding
  const knownBranch = !dc.company_id || branches.some((b) => b.company_id === dc.company_id);

  const save = async () => {
    setSaving(true);
    try {
      const {
        data: { data },
      } = await updateBdcDc(dc.dc_code, body);
      const picked = branches.find((b) => b.company_id === companyId);
      onSaved(
        data ?? {
          ...dc,
          dc_name: body.dc_name ?? dc.dc_name,
          ar_customer_code: body.ar_customer_code !== undefined ? body.ar_customer_code : dc.ar_customer_code,
          company_id: companyId || null,
          branch_code: companyId ? picked?.branch_code ?? null : null,
        },
      );
    } catch {
      // interceptor shows the reason (422 = สาขายังไม่ได้ลงทะเบียน)
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="tr" sx={unbound ? { "& td": { background: "#FFF6F6" } } : undefined}>
      <td className="dc">{dc.dc_code}</td>
      <td>
        {canWrite ? (
          <TextField
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ width: "100%", maxWidth: 360 }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        ) : (
          dc.dc_name
        )}
      </td>
      <td>
        {canWrite ? (
          <TextField
            select
            size="small"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            error={companyId === UNBOUND}
            sx={{ minWidth: 220 }}
            slotProps={{
              htmlInput: { "aria-label": `สาขาของ ${dc.dc_code}` },
              select: { displayEmpty: true },
            }}
          >
            <MenuItem value={UNBOUND}>
              <em>ยังไม่ผูก</em>
            </MenuItem>
            {!knownBranch && dc.company_id && (
              <MenuItem value={dc.company_id}>{dc.branch_code ?? `#${dc.company_id}`}</MenuItem>
            )}
            {branches.map((branch) => (
              <MenuItem key={branch.company_id} value={branch.company_id}>
                {branchLabel(branch)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <BranchChip branches={branches} companyId={dc.company_id} branchCode={dc.branch_code} />
        )}
      </td>
      <td className={canWrite ? undefined : "mono"}>
        {canWrite ? (
          <TextField
            size="small"
            value={arCode}
            placeholder="1012C00001"
            onChange={(e) => setArCode(e.target.value)}
            error={Boolean(arError)}
            helperText={arError}
            sx={{ width: 150 }}
            slotProps={{ htmlInput: { maxLength: 20, "aria-label": `รหัสลูกค้าบัญชีของ ${dc.dc_code}` } }}
          />
        ) : (
          dc.ar_customer_code || <span className="sub">ยังไม่ตั้ง</span>
        )}
      </td>
      <td className="n">{dc.store_count ?? "-"}</td>
      {canWrite && (
        <td className="c">
          <Button size="small" variant="contained" disabled={!dirty} loading={saving} onClick={save}>
            บันทึก
          </Button>
        </td>
      )}
    </Box>
  );
}

/**
 * DC master — created by import. Edit the display name and bind each DC to the BDC branch that
 * delivers it; a DC with no branch (ยังไม่ผูก) stops every import that holds it.
 */
export default function DcTab({
  canWrite,
  branches,
  onBindingChanged,
}: Readonly<{ canWrite: boolean; branches: BdcBranch[]; onBindingChanged?: () => void }>) {
  const { dcs, loading, setDcs } = useBdcDcs();
  const onSaved = (saved: BdcDc) => {
    setDcs((current) => current.map((dc) => (dc.dc_code === saved.dc_code ? saved : dc)));
    onBindingChanged?.();
  };
  const unbound = dcs.filter((dc) => !dc.company_id);

  return (
    <Box>
      <Typography className="sub" sx={{ px: 2, py: 1.25 }}>
        รหัส DC และชื่อมาจากไฟล์ EDI (สร้างให้อัตโนมัติตอนนำเข้า) · แก้ชื่อที่ใช้แสดงได้ · เลือกสาขาที่จัดส่งของแต่ละ DC ·
        รหัสลูกค้าบัญชีใช้ในใบวางบิล (DC ที่ยังไม่ตั้ง ออกใบวางบิลไม่ได้)
      </Typography>
      {unbound.length > 0 && (
        <Alert severity="warning" sx={{ mx: 2, mb: 1.25 }}>
          มี {pcs(unbound.length)} DC ที่ยังไม่ผูกสาขา ({unbound.map((dc) => dc.dc_code).join(", ")}) —
          ไฟล์ที่มี DC เหล่านี้นำเข้าไม่ได้จนกว่าจะเลือกสาขา
          {!canWrite && " · ให้หัวหน้า BDC เป็นผู้ผูก"}
        </Alert>
      )}
      {canWrite && branches.length === 0 && !loading && (
        <Alert severity="info" sx={{ mx: 2, mb: 1.25 }}>
          ยังไม่มีสาขา BDC ให้เลือก — ให้ MASTER ลงทะเบียนสาขาที่แท็บ สาขา ก่อน
        </Alert>
      )}
      {loading && <LinearProgress />}
      <table className="bdc-t">
        <thead>
          <tr>
            <th style={{ width: 120 }}>DC</th>
            <th>ชื่อ</th>
            <th>สาขาที่จัดส่ง</th>
            <th>รหัสลูกค้าบัญชี</th>
            <th className="n">ร้าน</th>
            {canWrite && <th style={{ width: 120 }} />}
          </tr>
        </thead>
        <tbody>
          {dcs.length === 0 && !loading ? (
            <tr>
              <td colSpan={6} className="c sub">
                ยังไม่มี DC — นำเข้าไฟล์ก่อน
              </td>
            </tr>
          ) : (
            dcs.map((dc) => (
              <DcRow
                key={`${dc.dc_code}-${dc.dc_name}-${dc.company_id ?? UNBOUND}-${dc.ar_customer_code ?? ""}`}
                dc={dc}
                branches={branches}
                canWrite={canWrite}
                onSaved={onSaved}
              />
            ))
          )}
        </tbody>
      </table>
    </Box>
  );
}
